import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBoxesStacked,
  faChevronRight,
  faCubesStacked,
  faTriangleExclamation,
  faTruckLoading,
  faPlus,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { receivingLotService } from '@/services/receiving-lot.service';
import { PageHeader } from '@/components/shared/PageHeader';
import { Breadcrumb } from '@/components/shared/Breadcrumb';
import { EmptyState } from '@/components/shared/EmptyState';
import { SkeletonList } from '@/components/skeletons/SkeletonList';
import { Button } from '@/components/ui/button';
import { useHasPermission } from '@/hooks/use-permissions';
import { toast } from '@/lib/swal';
import { getApiErrorMessage } from '@/lib/api-error';
import { ROUTES } from '@/routes/routes';
import { AddArrivalDialog } from './components/AddArrivalDialog';
import { ReopenLotDialog } from './components/ReopenLotDialog';

const STATUS_LABEL: Record<string, string> = {
  OPEN: 'Open',
  RECEIVING_COMPLETE: 'Receiving Complete',
  CLOSED: 'Closed',
};

const STATUS_BADGE_CLASS: Record<string, string> = {
  OPEN: 'bg-primary/10 text-primary',
  RECEIVING_COMPLETE: 'bg-amber-500/10 text-amber-600',
  CLOSED: 'bg-muted text-muted-foreground',
};

export function ReceivingLotDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const can = useHasPermission();

  const [addArrivalLotId, setAddArrivalLotId] = useState<string | null>(null);
  const [reopenLotId, setReopenLotId] = useState<string | null>(null);

  const {
    data: lot,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['receiving-lot', id],
    queryFn: () => receivingLotService.get(id!),
    enabled: Boolean(id),
  });

  const { data: arrivalsPage, isLoading: arrivalsLoading } = useQuery({
    queryKey: ['receiving-lot-arrivals', id],
    queryFn: () => receivingLotService.listArrivals(id!, { page: 1, pageSize: 50 }),
    enabled: Boolean(id),
  });

  const markReceivingCompleteMutation = useMutation({
    mutationFn: () => receivingLotService.markReceivingComplete(id!),
    onSuccess: () => {
      toast.success('Marked as receiving-complete');
      queryClient.invalidateQueries({ queryKey: ['receiving-lot', id] });
    },
    onError: (e: Error) => toast.error(getApiErrorMessage(e, 'Failed to update receiving lot')),
  });

  const closeMutation = useMutation({
    mutationFn: () => receivingLotService.close(id!),
    onSuccess: () => {
      toast.success('Receiving lot closed');
      queryClient.invalidateQueries({ queryKey: ['receiving-lot', id] });
    },
    onError: (e: Error) => toast.error(getApiErrorMessage(e, 'Failed to close receiving lot')),
  });

  const arrivals = arrivalsPage?.data ?? [];
  const isPartiallyReceived = lot?.status === 'OPEN' && lot.arrivalCount > 1;

  return (
    <div className="space-y-5">
      <Breadcrumb
        segments={[
          { label: 'Receiving Lots', to: ROUTES.OPS_RECEIVING_LOTS },
          { label: lot?.lotNumber ?? 'Lot' },
        ]}
      />

      {isLoading && <SkeletonList rows={4} />}

      {isError && (
        <EmptyState
          icon={faTriangleExclamation}
          title="Receiving Lot not found"
          description="It may have been removed, or you may not have access to it."
        />
      )}

      {lot && (
        <>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <PageHeader
              title={lot.lotNumber}
              description={`Opened ${new Date(lot.openingArrivalAt).toLocaleString()}`}
            />
            <div className="flex flex-wrap gap-2">
              {lot.status === 'OPEN' && can('receiving-lot:create') && (
                <Button size="sm" onClick={() => setAddArrivalLotId(lot.id)}>
                  <FontAwesomeIcon icon={faPlus} className="mr-2 h-3.5 w-3.5" />
                  Add Arrival
                </Button>
              )}
              {lot.status === 'OPEN' && can('receiving-lot:receiving-complete') && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => markReceivingCompleteMutation.mutate()}
                  disabled={markReceivingCompleteMutation.isPending}
                >
                  {markReceivingCompleteMutation.isPending && (
                    <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />
                  )}
                  Mark Receiving Complete
                </Button>
              )}
              {lot.status === 'RECEIVING_COMPLETE' && can('receiving-lot:close') && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => closeMutation.mutate()}
                  disabled={closeMutation.isPending}
                >
                  {closeMutation.isPending && (
                    <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />
                  )}
                  Close Lot
                </Button>
              )}
              {(lot.status === 'RECEIVING_COMPLETE' || lot.status === 'CLOSED') &&
                can('receiving-lot:reopen') && (
                  <Button size="sm" variant="ghost" onClick={() => setReopenLotId(lot.id)}>
                    Reopen / Late Arrival
                  </Button>
                )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">Customer</p>
              <p className="mt-1 text-sm font-medium text-foreground">{lot.customerName}</p>
              {lot.customerLocationName && (
                <p className="text-xs text-muted-foreground">{lot.customerLocationName}</p>
              )}
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">Status</p>
              <p className="mt-1">
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE_CLASS[lot.status] ?? 'bg-muted text-muted-foreground'}`}
                >
                  {STATUS_LABEL[lot.status] ?? lot.status}
                </span>
                {isPartiallyReceived && (
                  <span className="ml-2 rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-600">
                    Partially Received
                  </span>
                )}
              </p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">Total Tools</p>
              <p className="mt-1 text-sm font-medium text-foreground">{lot.equipmentCount}</p>
            </div>
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">Model Batches</p>
              <p className="mt-1 text-sm font-medium text-foreground">{lot.modelBatches.length}</p>
            </div>
          </div>

          {lot.notes && (
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">Notes</p>
              <p className="mt-1 text-sm text-foreground">{lot.notes}</p>
            </div>
          )}

          <div>
            <h2 className="mb-3 text-sm font-semibold text-foreground">
              Arrivals ({lot.arrivalCount})
            </h2>
            {arrivalsLoading ? (
              <SkeletonList rows={2} />
            ) : arrivals.length === 0 ? (
              <EmptyState icon={faTruckLoading} title="No arrivals recorded" />
            ) : (
              <div className="space-y-2">
                {arrivals.map((arrival) => (
                  <div
                    key={arrival.id}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-4"
                  >
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        Arrival #{arrival.arrivalSequence}
                        <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                          {arrival.status}
                        </span>
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        Received {new Date(arrival.receivedAt).toLocaleString()}
                        {arrival.courierName && ` · ${arrival.courierName}`}
                        {arrival.trackingNumber && ` · ${arrival.trackingNumber}`}
                        {arrival.packageCount != null && ` · ${arrival.packageCount} package(s)`}
                      </p>
                      {arrival.remarks && (
                        <p className="mt-1 text-xs text-muted-foreground">{arrival.remarks}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-sm font-medium text-foreground">
                      <FontAwesomeIcon
                        icon={faBoxesStacked}
                        className="h-3 w-3 text-muted-foreground"
                      />
                      {arrival.equipmentCount} tool{arrival.equipmentCount === 1 ? '' : 's'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <h2 className="mb-3 text-sm font-semibold text-foreground">Model Batches</h2>
            {lot.modelBatches.length === 0 ? (
              <EmptyState icon={faCubesStacked} title="No Model Batches" />
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {lot.modelBatches.map((batch) => (
                  <button
                    key={batch.id}
                    onClick={() =>
                      navigate(
                        ROUTES.OPS_RECEIVING_LOT_MODEL_BATCH.replace(':lotId', lot.id).replace(
                          ':batchId',
                          batch.id,
                        ),
                      )
                    }
                    className="flex items-center justify-between gap-3 rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-primary/40 hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {batch.equipmentModelName ?? 'Unknown model'}
                      </p>
                      <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                        <FontAwesomeIcon icon={faBoxesStacked} className="h-3 w-3" />
                        {batch.equipmentCount} tool{batch.equipmentCount === 1 ? '' : 's'}
                      </p>
                    </div>
                    <FontAwesomeIcon
                      icon={faChevronRight}
                      className="h-3 w-3 text-muted-foreground"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <AddArrivalDialog
        lotId={addArrivalLotId}
        onClose={() => setAddArrivalLotId(null)}
        onAdded={() => setAddArrivalLotId(null)}
      />
      <ReopenLotDialog
        lotId={reopenLotId}
        onClose={() => setReopenLotId(null)}
        onReopened={() => setReopenLotId(null)}
      />
    </div>
  );
}
